import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Typography
} from 'extended-oxygen-elements';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DutyConfiguration, DutyRequirement } from '../types';
import { getDutyIconComponent } from 'lib/helpers/dutyIcons';
import { validateDutyRequirements } from '../utils/validation';
import * as FormStyles from '../styles/formStyles';

type ConfigureDutyModalProps = {
  open: boolean;
  config: DutyConfiguration | null;
  onClose: () => void;
  onSave: (updatedDuty: DutyConfiguration) => void;
  isSaving?: boolean;
  container?: HTMLElement | (() => HTMLElement | null) | null;
};

export function ConfigureDutyModal({
                                     open,
                                     config,
                                     onClose,
                                     onSave,
                                     isSaving = false,
                                     container
}: ConfigureDutyModalProps) {
  // Local state for form fields
  const [timePeriods, setTimePeriods] = useState<DutyRequirement[]>(
    config?.requirements || []
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  // Reset form when duty changes
  React.useEffect(() => {
    if (config) {
      setTimePeriods(config.requirements || []);
      setValidationErrors([]);
      setDeleteConfirmIndex(null);
    }
  }, [config]);

  const handleSave = useCallback(() => {
    if (!config || isSaving) return;

    // Validate form
    const { isValid, errors } = validateDutyRequirements(timePeriods);
    if (!isValid) {
      setValidationErrors(errors);
      return;
    }

    // Clear any previous validation errors
    setValidationErrors([]);

    const updatedDuty: DutyConfiguration = {
      ...config,
      requirements: timePeriods
    };

    onSave(updatedDuty);
  }, [
    config,
    timePeriods,
    onSave,
    isSaving,
    validateDutyRequirements
  ]);

  const handleAddTimePeriod = useCallback(() => {
    setTimePeriods([
      ...timePeriods,
      {
        startTime: '09:00',
        endTime: '17:00',
        minPersonnel: 1
      }
    ]);
  }, [timePeriods]);

  const handleRemoveTimePeriod = useCallback((index: number) => {
    setTimePeriods(timePeriods.filter((_, i) => i !== index));
    setDeleteConfirmIndex(null);
  }, [timePeriods]);

  const handleDeleteClick = useCallback((index: number) => {
    setDeleteConfirmIndex(index);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmIndex(null);
  }, []);

  const handleTimePeriodChange = useCallback(
    (index: number, field: keyof DutyRequirement, value: string | number) => {
      const updated = [...timePeriods];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      setTimePeriods(updated);
    },
    [timePeriods]
  );

  if (!config) return null;

  const DutyIcon = getDutyIconComponent(config.duty.key);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      container={container}
      style={{ position: container ? 'absolute' : 'fixed' }}
      PaperProps={{
        sx: {
          ...FormStyles.modalPaperStyles,
          margin: container ? '32px auto' : undefined
        }
      }}
    >
      {/* Header */}
      <Box sx={FormStyles.modalHeaderStyles}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {DutyIcon && <DutyIcon sx={{ fontSize: 24, color: 'rgba(0, 0, 0, 0.54)' }} />}
          <Typography variant="h6" component="div" sx={FormStyles.modalTitleStyles}>
            {config.duty.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={FormStyles.closeIconButtonStyles}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={FormStyles.modalContentStyles}>
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert severity="error" onClose={() => setValidationErrors([])} sx={{ mb: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Please fix the following errors:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          </Alert>
        )}

        {/* Time Periods Table */}
        <Box>
          {/* Table Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: FormStyles.tableGridColumns,
              gap: '12px',
              mb: 1
            }}
          >
            <Typography variant="body2" sx={FormStyles.tableHeaderTypographyStyles}>
              From
            </Typography>
            <Typography variant="body2" sx={FormStyles.tableHeaderTypographyStyles}>
              To
            </Typography>
            <Typography variant="body2" sx={FormStyles.tableHeaderTypographyStyles}>
              No. People
            </Typography>
            <Box /> {/* Empty space for delete icon column */}
          </Box>

          {/* Divider */}
          <Divider sx={FormStyles.tableDividerStyles} />

          {/* Time Period Rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {timePeriods.map((period, index) => (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: FormStyles.tableGridColumns,
                    gap: '8px',
                    alignItems: 'center',
                    pb: '8px'
                  }}
                >
                  <TextField
                    type="time"
                    value={period.startTime}
                    onChange={(e) =>
                      handleTimePeriodChange(index, 'startTime', e.target.value)
                    }
                    size="small"
                    fullWidth
                    InputProps={{ sx: FormStyles.textFieldInputStyles }}
                  />
                  <TextField
                    type="time"
                    value={period.endTime}
                    onChange={(e) =>
                      handleTimePeriodChange(index, 'endTime', e.target.value)
                    }
                    size="small"
                    fullWidth
                    InputProps={{ sx: FormStyles.textFieldInputStyles }}
                  />
                  <TextField
                    type="number"
                    value={period.minPersonnel}
                    onChange={(e) =>
                      handleTimePeriodChange(
                        index,
                        'minPersonnel',
                        parseInt(e.target.value) || 0
                      )
                    }
                    size="small"
                    fullWidth
                    inputProps={{ min: 1 }}
                    InputProps={{ sx: FormStyles.textFieldInputStyles }}
                  />
                  <IconButton
                    onClick={() => handleDeleteClick(index)}
                    size="small"
                    sx={FormStyles.deleteRowIconButtonStyles}
                    aria-label="Delete time period"
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
                {/* Divider after each row */}
                <Divider sx={FormStyles.tableDividerStyles} />
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={FormStyles.modalActionsStyles}>
        {/* Add Time Period Button */}
        <Button
          variant="text"
          size="medium"
          onClick={handleAddTimePeriod}
          startIcon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
          sx={{ ...FormStyles.textButtonStyles, padding: '6px 8px' }}
        >
          Add Time Period
        </Button>

        <Box sx={FormStyles.buttonGroupStyles}>
          <Button
            onClick={onClose}
            variant="text"
            disabled={isSaving}
            sx={{ ...FormStyles.textButtonStyles, minWidth: '64px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            sx={FormStyles.primaryButtonStyles}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Box>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmIndex !== null}
        onClose={handleCancelDelete}
        maxWidth="xs"
        container={container}
        style={{ position: container ? 'absolute' : 'fixed' }}
        PaperProps={{
          sx: {
            ...FormStyles.confirmationDialogPaperStyles,
            margin: container ? '32px auto' : undefined
          }
        }}
      >
        <DialogContent sx={FormStyles.confirmationDialogContentStyles}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Delete Time Period?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
            Are you sure you want to delete this time period?
          </Typography>
        </DialogContent>
        <DialogActions sx={FormStyles.confirmationDialogActionsStyles}>
          <Button
            onClick={handleCancelDelete}
            variant="text"
            sx={FormStyles.textButtonStyles}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteConfirmIndex !== null && handleRemoveTimePeriod(deleteConfirmIndex)}
            variant="contained"
            color="error"
            sx={FormStyles.destructiveButtonStyles}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
